import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NetworkRequest, Organization } from "../../../../../../common/models";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { VendorOutcomeOption } from "../../../../../../common/service/server/Types";
import { useAuthorizations } from "../../../../../hooks/useAuthorizations";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { useOrganizationVendors } from "../../../../../hooks/useOrganizationVendors";
import { Routes } from "../../../../../root/routes";
import { sortVendorsByCategory } from "../../../../../util/vendorCategorization";
import { Abbr } from "../../../../Abbr";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { SurveyAnswers, useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import {
  getRequestProcessingRegion,
  isRequestContextApplicableWithCrunchFiltering,
} from "../../../../../util/vendors";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";

import { useManagedAdNetworks } from "../../../../../../common/hooks/dataRecipientSettingsHooks";
import { parseMultiselectAnswerWithNoneOption } from "../../../../../surveys/survey";
import { VendorDeleteRow } from "../shared/VendorDeleteRow";
import { RetainedVendorRow } from "../shared/RetainedVendorRow";
import { CategorizedVendorList } from "../shared/CategorizedVendorList";
import { useInstructionsState } from "../shared/useInstructionsState";
import { DataSubjectRequestDetailDto } from "../../../../../../common/service/server/dto/DataSubjectRequestDto";
import { DataMapDto } from "../../../../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationManagedAdNetworkDto } from "../../../../../../common/service/server/dto/OrganizationManagedAdNetworkDto";

export type VendorOutcomeHandler = (
  vendor: OrganizationDataRecipientDto,
  outcome: VendorOutcomeOption,
) => void;

export type VendorOutcomeCollection = Record<string, string>;

const getAdManagerAndManagedVendorIds = (
  allVendors: OrganizationDataRecipientDto[],
  managedAdNetworks: OrganizationManagedAdNetworkDto[],
  sellingAndSharingAnswers: SurveyAnswers,
): {
  customAudienceVendorManagers: string[];
  managedAdNetworksIds: string[];
  managementVendors: OrganizationDataRecipientDto[];
} => {
  const customAudienceVendorManagers = parseMultiselectAnswerWithNoneOption(
    sellingAndSharingAnswers,
    "custom-audience-feature-separate-vendor",
  );

  // Get all network IDs that are managed by someone (only if the manager is still selected)
  const managedAdNetworksIds: string[] = [
    ...new Set(
      managedAdNetworks
        ?.filter((item) => customAudienceVendorManagers.includes(item.managerId))
        ?.map((item) => item.networkId) || [],
    ),
  ];

  // Get all unique manager IDs
  const managementVendorIds: string[] = [
    ...new Set(managedAdNetworks?.map((item) => item.managerId) || []),
  ];

  // Filter management vendors to only include those that are still selected in the survey
  const managementVendors =
    allVendors?.filter(
      (v) => managementVendorIds.includes(v.id) && customAudienceVendorManagers.includes(v.id),
    ) || [];

  return { customAudienceVendorManagers, managedAdNetworksIds, managementVendors };
};

export const vendorsForDeleteInfoStep = (
  allVendors: OrganizationDataRecipientDto[],
  request: DataSubjectRequestDetailDto, // if null, will skip request-specific filters
  dataMap: DataMapDto,
  sellingAndSharingAnswers: SurveyAnswers,
  managedAdNetworks: OrganizationManagedAdNetworkDto[],
  organization: Organization,
): OrganizationDataRecipientDto[] => {
  const { managedAdNetworksIds, managementVendors } = getAdManagerAndManagedVendorIds(
    allVendors,
    managedAdNetworks,
    sellingAndSharingAnswers,
  );

  let result = allVendors.filter((vendor) => {
    // First do checks that are request-specific
    if (request) {
      const requestProcessingRegion = getRequestProcessingRegion(request);

      if (!vendor.processingRegions.includes(requestProcessingRegion)) {
        return false;
      }

      const contextOk = isRequestContextApplicableWithCrunchFiltering(
        dataMap,
        request.context,
        vendor,
        request,
        organization,
      );

      if (!contextOk) {
        return false;
      }
    }

    // Skip internal storage locations
    if (vendor.dataRecipientType === "storage_location" && vendor.isCustom) {
      return false;
    }

    // If a management vendor exists, hide vendors that are managed by the management vendor
    // (similar to DeleteInstructionsPage and OptOutInstructionsPage)
    if (managementVendors.length > 0 && managedAdNetworksIds.includes(vendor.id)) {
      return false;
    }

    const instruction = vendor.instructions.find((vi) => vi.requestType == "DELETE");
    const instructionOk = instruction?.processingMethod != "INACCESSIBLE_OR_NOT_STORED";
    const isDataDeletable = vendor.dataDeletability !== "NOT_DELETABLE";
    // Since some data accessibility values could be null or UNKNOWN, and we still want to show those in the vendor list, we only check that the value is *not* NOT_ACCESSIBLE (instead of only filtering strictly on ACCESSIBLE)
    const isDataInaccessible = vendor.dataAccessibility === "NOT_ACCESSIBLE";
    return instructionOk && isDataDeletable && !isDataInaccessible;
  });

  // Add management vendors that should appear in deletion workflows (similar to opt-out workflow)
  if (managementVendors.length > 0 && managedAdNetworksIds.length > 0) {
    const managementVendorsToAdd = managementVendors.filter((vendor) => {
      // Skip if already in vendors list
      if (result?.find((v) => v.id === vendor.id)) {
        return false;
      }

      // Use simpler criteria like opt-out workflow - just check accessibility and that there are managed networks
      return managedAdNetworksIds.length > 0 && vendor.dataAccessibility !== "NOT_ACCESSIBLE";
    });

    if (managementVendorsToAdd.length > 0) {
      result?.push(...managementVendorsToAdd);
    }
  }

  return result;
};

export const canVendorHaveDataDeleted = (vendor: OrganizationDataRecipientDto): boolean => {
  // A DELETE instruction with `processingMethod="RETAIN"` and `hasRetentionExceptions=false` means that either the data must be retained or the data cannot be deleted, and therefore should *not* be included in the list of delete vendors (i.e. this excludes vendors whose corresponding data deletability in the Data Storage settings table is either "No - data must be retained" or "No - unable to delete data")
  return !vendor.instructions.some(
    (i) =>
      i.requestType == "DELETE" && i.processingMethod === "RETAIN" && !i.hasRetentionExceptions,
  );
};

export const DeleteInfo = () => {
  const [org, orgRequest] = usePrimaryOrganization();
  const [allVendors, vendorsRequest] = useOrganizationVendors(org.id, undefined, true);
  const { request, setStepCompleted, mutations } = useDataRequestProcessingContext();
  const [readyToRender, setReadyToRender] = useState(false);
  const { data: dataMap } = useDataMap(org.id);
  const instructionsState = useInstructionsState();

  const { answers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);
  const { managedAdNetworks } = useManagedAdNetworks(org.id);

  const { authorizations, request: authorizationsRequest } = useAuthorizations(org.id);

  const onSelectionChange: VendorOutcomeHandler = (vendor, value) => {
    mutations.updateVendorOutcomeMutation.mutate({ vendor: vendor.vendorId, outcome: value });
  };

  // Effects
  useEffect(() => {
    if (NetworkRequest.areFinished(vendorsRequest, orgRequest, authorizationsRequest)) {
      setReadyToRender(true);
    }
  }, [orgRequest, vendorsRequest, authorizationsRequest]);

  const { customAudienceVendorManagers, managementVendors } = getAdManagerAndManagedVendorIds(
    allVendors,
    managedAdNetworks,
    answers,
  );

  const vendors = vendorsForDeleteInfoStep(
    allVendors,
    request,
    dataMap,
    answers,
    managedAdNetworks,
    org,
  );

  const installedVendors = useMemo(() => {
    return sortVendorsByCategory(vendors?.filter((v) => !v.isInstalled) ?? []);
  }, [vendors]);

  const deleteVendors = useMemo(() => {
    return installedVendors.filter(canVendorHaveDataDeleted);
  }, [installedVendors]);

  const retainVendors = useMemo(() => {
    return installedVendors.filter((v) =>
      v.instructions.some((i) => i.requestType == "DELETE" && i.processingMethod === "RETAIN"),
    );
  }, [installedVendors]);

  // Vendors that will show up in both deleteVendors and retainVendors: DELETE instruction with `processingMethod="RETAIN"` and `hasRetentionExceptions=true` (i.e. the data is partially deleted)

  useEffect(() => {
    const outcomes = request.vendorOutcomes;
    const everyOutcomeCompleted = deleteVendors.every((vendor) => {
      const outcome = outcomes?.find((o) => o.vendor.id == vendor.vendorId);
      let result =
        (outcome?.outcome != null && outcome?.outcome !== "AUTOMATION_FAILED") ||
        vendor.isInstalled === true;
      return result;
    });

    setStepCompleted(
      Boolean(deleteVendors) && deleteVendors.length <= outcomes.length && everyOutcomeCompleted,
    );
  }, [setStepCompleted, deleteVendors, request.vendorOutcomes, vendors]);

  const vendorOutcomes = useMemo(() => {
    const outcomes = request.vendorOutcomes.reduce(
      (acc, outcome) => Object.assign({}, acc, { [outcome.vendor.id]: outcome.outcome }),
      {},
    );

    request._local?.pendingVendorOutcomeUpdates?.forEach((pending) => {
      outcomes[pending.vendorId] = pending.outcome;
    });

    return outcomes;
  }, [request.vendorOutcomes, request._local?.pendingVendorOutcomeUpdates]);

  const pendingUpdates = useMemo(() => {
    const pending = request._local?.pendingVendorOutcomeUpdates?.map((pending) => pending.vendorId);
    return new Set(pending);
  }, [request._local?.pendingVendorOutcomeUpdates]);

  if (!readyToRender) return null;

  return (
    <>
      <div className="text-t3 mb-std">
        Select how you’ve processed data handled by each Data Recipient. Your processing defaults
        are marked; select them only if they apply for this request. Your defaults and instructions
        can be updated in{" "}
        <Link className="text-weight-regular" to="/organization/requestHandling">
          Request Instructions.
        </Link>
      </div>

      {deleteVendors && (
        <CategorizedVendorList
          vendors={deleteVendors}
          renderVendor={(vendor) => {
            // Check if this vendor is a management vendor
            const isManagementVendor = managementVendors.some((v) => v.id === vendor.id);
            const customAudienceManagementVendor = isManagementVendor ? vendor : undefined;

            // Get only the networks managed by THIS specific vendor
            const networksManaged = customAudienceManagementVendor
              ? managedAdNetworks
                  ?.filter(
                    (item) =>
                      item.managerId === vendor.id &&
                      customAudienceVendorManagers?.includes(item.managerId),
                  )
                  ?.map((item) => allVendors?.find((v) => v.id === item.networkId))
                  ?.filter(Boolean)
              : undefined;

            return (
              <VendorDeleteRow
                key={vendor.id}
                vendor={vendor}
                authorizations={authorizations}
                vendorOutcomes={vendorOutcomes}
                updatePending={pendingUpdates.has(vendor.vendorId)}
                onSelectionChange={onSelectionChange}
                org={org}
                request={request}
                managedCustomAudienceVendors={networksManaged}
                customAudienceManagementVendor={customAudienceManagementVendor}
                instructionsState={instructionsState}
              />
            );
          }}
        />
      )}

      {retainVendors && retainVendors.length > 0 && (
        <>
          <div className="text-t5 text-weight-medium mb-md"> Data Retention </div>

          <div className="text-t3 mb-mdlg">
            The systems below are marked for retention based on one or more{" "}
            <Abbr title="Your business may retain personal data under certain exceptions, such as for internal use and to comply with a legal obligation.">
              <strong>exceptions to deletion</strong>
            </Abbr>
            . Do not delete data stored with these Data Recipients if it is kept under an exception
            to deletion. Data retention settings can be updated in your{" "}
            <Link to={Routes.workflows.dataStorageSettings}>Data Storage settings</Link>.
          </div>

          <CategorizedVendorList
            vendors={retainVendors}
            renderVendor={(vendor) => <RetainedVendorRow key={vendor.id} vendor={vendor} />}
          />
        </>
      )}
    </>
  );
};
